
import { TenantSystemPrefs, UserPermissions } from "@/store/types";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter, State } from "vuex-class";
@Component({})
export default class InteractiveConsole extends Vue {
    @Getter("getFeature") private getFeature: (featureName: string[]) => boolean;
    @Getter("getPermissions") private getPermissions: UserPermissions;
    @State("tenantSystemPrefs") private tenantSystemPrefs: TenantSystemPrefs;

    private phoneNumber: string = "";
    private showIframe: boolean = true;

    private get interactiveConsoleURL(): string {
        return this.tenantSystemPrefs.interactiveConsoleURL;
    }

    @Watch("interactiveConsoleURL")
    private reloadIfFrame(): void {
        this.showIframe = false;
        this.$nextTick(() => {
            this.showIframe = true;
        });
    }

    private get canAccessInteractiveConsole(): boolean {
        return this.getFeature(["InteractiveConsole"]) && this.getPermissions.canAccessInteractiveConsole;
    }

    private get isUrlValid(): boolean {
        try {
            if (!this.interactiveConsoleURL) {
                return false;
            }

            new URL(this.interactiveConsoleURL);
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Sizes the relay and points its iframe at the console.
     *
     * The console's URL is a customer-edited preference, so the application's
     * own frame-src cannot name it. console-frame.html is served with a policy
     * that permits framing an https origin, and setting its iframe from here
     * works because the relay is same-origin — only same-origin code can reach
     * contentDocument, so the browser enforces that without a message protocol.
     *
     * That policy denies stylesheets as well, so the relay carries no CSS and
     * the layout is applied here. Writing through the CSSOM is not a style-src
     * decision, unlike a stylesheet or a style attribute in the markup.
     */
    private onRelayLoad(): void {
        const relay = this.$refs.icIframe as HTMLIFrameElement | undefined;
        const doc = relay && relay.contentDocument;

        if (!doc) {
            return;
        }

        doc.documentElement.style.height = "100%";
        doc.body.style.height = "100%";
        doc.body.style.margin = "0";
        doc.body.style.overflow = "hidden";

        const frame = doc.getElementById("console") as HTMLIFrameElement | null;
        if (!frame) {
            return;
        }

        // Without this the frame sits on a text baseline and overflows the body.
        frame.style.display = "block";

        if (this.isUrlValid) {
            frame.setAttribute("src", this.interactiveConsoleURL);
        }
    }

    /**
     * The console's own window, which is a frame inside the relay rather than the
     * relay itself. Messages must reach the console, so addressing the relay's
     * contentWindow would post into a page that has no script to receive it.
     */
    private consoleWindow(): Window | null {
        const relay = this.$refs.icIframe as HTMLIFrameElement | undefined;
        const doc = relay && relay.contentDocument;

        if (!doc) {
            return null;
        }

        const frame = doc.getElementById("console") as HTMLIFrameElement | null;
        return frame ? frame.contentWindow : null;
    }

    private validatePhoneNumber(phoneNumber: string): boolean {
        const phoneRegex = /^\+?[1-9]\d{1,14}$/; // E.164 format
        return phoneRegex.test(phoneNumber);
    }

    public makeCall(iframe: boolean = false) {
        if (!this.validatePhoneNumber(this.phoneNumber)) {
            this.$notify({
                type: "error",
                title: "Invalid Phone Number",
                text: "Please enter a valid phone number in E.164 format (e.g., +1234567890)."
            });
            return;
        }

        const kvps = { };
        const data = {
            phoneNumber: this.phoneNumber,
            display: "0",
            kvps: kvps
        };

        const icJsonRequest = {
            name: "makeCall",
            data: data
        };

        if (iframe) {
            const consoleWindow = this.consoleWindow();

            if (consoleWindow) {
                consoleWindow.postMessage(icJsonRequest, "*");
            } else {
                console.error("IC frame is not open.");
            }
        } 
    }
}
